<template>
  <div class="page">
    <div class="list-body">
      <!--
        WHY the form opts out of the browser's own validation (novalidate):
        every way this screen can fail has to end in a message that this
        component renders, so that no path is ever silent. Native validation
        would intercept the submit with a browser bubble, and the operator
        would get a different message from a different place depending on the
        browser. The `required` attributes stay on the inputs so assistive
        technology still announces which fields are mandatory.
      -->
      <form @submit.prevent="submitForm" class="row" novalidate>
        <div class="col-md-12 d-flex flex-column gap-2">
          <div class="form-floating">
            <!--
              WHY the code field is a text input with a numeric keypad hint
              rather than a number input: a number input drops a leading zero
              and returns a number to the model, which would break the 6-digit
              code shape the backend issues. `autocomplete="one-time-code"`
              lets a phone offer the code that just arrived on WhatsApp.
            -->
            <input type="text"
                   v-model="formDate.token"
                   class="form-control"
                   id="resetCodeInput"
                   inputmode="numeric"
                   spellcheck="false"
                   autocomplete="one-time-code"
                   aria-describedby="resetCodeHint"
                   placeholder="6-digit code"
                   required>
            <label for="resetCodeInput">6-digit code from WhatsApp</label>
          </div>
          <p class="form-hint" id="resetCodeHint">
            We sent this code to the WhatsApp number on your account.
          </p>

          <div class="form-floating">
            <input :type="newPasswordFieldType"
                   v-model="formDate.newPassword"
                   class="form-control"
                   id="newPasswordInput"
                   autocomplete="new-password"
                   aria-describedby="passwordRequirementsHint"
                   placeholder="New password"
                   required>
            <label for="newPasswordInput">New password</label>
          </div>
          <label class="show-password-checkbox" for="showNewPassword">
            <input type="checkbox" id="showNewPassword" v-model="show_new_password">
            Show new password
          </label>

          <div class="form-floating">
            <input :type="confirmPasswordFieldType"
                   v-model="confirmPassword"
                   class="form-control"
                   id="confirmPasswordInput"
                   autocomplete="new-password"
                   aria-describedby="passwordMatchHint"
                   placeholder="Confirm new password"
                   required>
            <label for="confirmPasswordInput">Confirm new password</label>
          </div>
          <label class="show-password-checkbox" for="showConfirmPassword">
            <input type="checkbox" id="showConfirmPassword" v-model="show_confirm_password">
            Show confirmation
          </label>

          <p class="form-hint" id="passwordRequirementsHint">
            Password guidance: use at least 8 characters. Every space you type counts, including a
            space at the start or the end.
          </p>
          <!--
            WHY the live match message lives in its own element that is always
            in the document: a live region has to exist before its content
            changes, otherwise a screen reader never announces the update.
          -->
          <p class="form-hint" :class="matchFeedbackClass" id="passwordMatchHint" role="status"
             aria-live="polite">
            {{ matchFeedback.text }}
          </p>
        </div>
        <div class="container mt-4">
          <!--
            WHY the button is disabled while a request is in flight: the reset
            code is single-use, so a second click would spend the same code
            twice and the second attempt would fail for a reason the operator
            cannot act on.
          -->
          <button :disabled="loading" type="submit" class="btn btn-primary w-100">
            <span v-if="loading" class="spinner-grow spinner-grow-sm" role="status" aria-hidden="true"></span>
            SAVE
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import Swal from "sweetalert2";
import { responses } from "@/services/responses";

/**
 * The exact shape of the reset code the backend issues (a 6-digit string
 * generated with %06d and delivered over WhatsApp through the Meta
 * standard_otp template). The same shape is used to decide whether a value
 * that arrived in the route query may be pre-filled into the code field.
 */
const RESET_CODE_PATTERN = /^\d{6}$/;

export default {
  name: "ResetPassword",
  computed: {
    /**
     * The type the new-password input renders as. Mirrors the show-password
     * pattern already used on the login form, so the two screens behave the
     * same way for the person at the keyboard.
     *
     * @returns {string} "text" while the password is revealed, "password"
     *   otherwise.
     */
    newPasswordFieldType() {
      return this.show_new_password ? "text" : "password";
    },

    /**
     * The type the confirmation input renders as.
     *
     * @returns {string} "text" while the confirmation is revealed, "password"
     *   otherwise.
     */
    confirmPasswordFieldType() {
      return this.show_confirm_password ? "text" : "password";
    },

    /**
     * The live match feedback for the two password fields.
     *
     * The two values are compared RAW. Trimming them would report a match
     * that the backend does not see, and it would invite a "fix" that trims
     * the password on submit -- leading and trailing spaces are legitimate
     * password characters.
     *
     * @returns {{state: string, text: string}} the feedback state ("idle",
     *   "pending", "match" or "mismatch") and the message to show.
     */
    matchFeedback() {
      const newPassword = this.formDate.newPassword;
      const confirmation = this.confirmPassword;

      if (newPassword === "" && confirmation === "") {
        return { state: "idle", text: "" };
      }
      if (confirmation === "") {
        return { state: "pending", text: "Type your new password again to confirm it." };
      }
      if (newPassword === "") {
        return { state: "pending", text: "Type your new password first." };
      }
      if (newPassword === confirmation) {
        return { state: "match", text: "Both entries match." };
      }
      return {
        state: "mismatch",
        text: "The two passwords don't match. Please retype them so both entries are the same.",
      };
    },

    /**
     * The styling hook for the live match message.
     *
     * @returns {string} the extra class that colours the message for its
     *   state, or an empty string for the neutral state.
     */
    matchFeedbackClass() {
      if (this.matchFeedback.state === "match") {
        return "form-hint-match";
      }
      if (this.matchFeedback.state === "mismatch") {
        return "form-hint-mismatch";
      }
      return "";
    },
  },
  data() {
    return {
      // WHY the confirmation value is held here and not inside `formDate`:
      // `formDate` is the object handed to the Vuex `confirmReset` action and
      // the action mutates that very object in place (it adds its own
      // `localID`). Keeping every piece of screen state that is not part of
      // the wire payload outside that object keeps the dispatched payload at
      // exactly { token, newPassword }.
      confirmPassword: "",
      formDate: {
        newPassword: "",
        token: this.prefillResetCode(),
      },
      loading: false,
      show_new_password: false,
      show_confirm_password: false,
    };
  },
  methods: {
    /**
     * Reads the reset code that may be handed over in the route query.
     *
     * The code is pre-filled ONLY when it already has the exact 6-digit shape
     * the backend issues. Anything else -- no query at all, a repeated query
     * parameter, an address, a stale link -- is ignored, because a pre-filled
     * box that does not look like a code is exactly what led operators to
     * type the wrong value into this field (TicketApp #29691).
     *
     * @returns {string} the 6-digit code, or an empty string when the query
     *   carries nothing that can be a code.
     */
    prefillResetCode() {
      const raw = this.$route && this.$route.query ? this.$route.query.token : "";
      if (typeof raw !== "string") {
        // A repeated query parameter arrives as an array, and a missing one as
        // undefined. Neither can be a code.
        return "";
      }
      return RESET_CODE_PATTERN.test(raw) ? raw : "";
    },

    /**
     * Records a reset attempt with the usage tracker.
     *
     * The tracker is published on `window` by GlobalVariables.js and has no
     * module export with the same payload shape, so the window reference is
     * kept as it is. Swapping the tracking mechanism is not part of this task.
     *
     * @param {string} action the attempt outcome ("submit", "success" or
     *   "fail").
     */
    trackReset(action) {
      if (window.usageTracker) {
        window.usageTracker.track("kdiy-1777981450768", { feature: "auth:resetPassword", action: action });
      }
    },

    /**
     * Checks the form before any request leaves the browser.
     *
     * Nothing is trimmed or normalised here: the value the operator typed is
     * the value that is validated and the value that is sent, so the screen
     * never quietly changes what somebody entered.
     *
     * @returns {string} an empty string when the form is ready to submit, or
     *   the guidance to show the operator.
     */
    validateForm() {
      const code = this.formDate.token;

      if (!code) {
        return "Enter the 6-digit code we sent to your WhatsApp number.";
      }
      if (!RESET_CODE_PATTERN.test(code)) {
        return "That code should be 6 digits. Please check the WhatsApp message and enter it again.";
      }
      if (!this.formDate.newPassword) {
        return "Enter your new password.";
      }
      if (!this.confirmPassword) {
        return "Type your new password again to confirm it.";
      }
      if (this.formDate.newPassword !== this.confirmPassword) {
        return "The two passwords don't match. Please retype them so both entries are the same.";
      }
      return "";
    },

    /**
     * Shows the guidance for a form that is not ready to be submitted.
     *
     * The wording tells the operator what to do next and never says that
     * they did something wrong.
     *
     * @param {string} message the guidance to display.
     * @returns {Promise} the SweetAlert2 result promise for the dialog.
     */
    showGuidanceModal(message) {
      return Swal.fire({
        icon: "info",
        title: "Check the form",
        text: message,
        showCloseButton: true,
      });
    },

    /**
     * Reduces a failed request to a short detail that can be shown to the
     * person at the keyboard.
     *
     * A stack trace, a long blob or an unrendered object is never useful
     * guidance and is dropped; only a short, single-line message survives.
     *
     * @param {Error} err the rejection raised by the failed request.
     * @returns {string} the detail to show, or an empty string when there is
     *   nothing safe to show.
     */
    readableFailureDetail(err) {
      const raw = err && typeof err.message === "string" ? err.message : "";
      const candidate = raw.trim();

      if (candidate === "" || candidate === "[object Object]") {
        return "";
      }
      if (candidate.indexOf("\n") !== -1 || candidate.length > 160) {
        return "";
      }
      return candidate;
    },

    /**
     * Shows this component's own error modal for a failed reset.
     *
     * The component owns this modal because the SUBMIT leg of the reset
     * runs the shared error handler with the `isSweetalert` flag set (task
     * 003, S1 seam), so the shared handler opens NO dialog on this path -
     * this modal is the ONLY error dialog the operator sees.
     *
     * The open-then-close race that used to swallow the shared dialog is
     * gone: task 005 (commit b0899f5) removed the force-close, so the
     * dialog the shared handler opens on the legs that still use it now
     * STAYS visible, and SweetAlert2's replace-on-fire governs the
     * lifetime of whichever modal is open.
     *
     * UNTRUSTED TEXT RULE: the detail shown here comes from the server, so it
     * is NEVER handed to SweetAlert2 as an HTML string. The `footer` option
     * is an HTML sink -- a string is parsed with DOMParser and the resulting
     * nodes are moved into the live document -- so a server-originated message
     * would be able to carry markup and event handlers. The detail is
     * therefore put into a DOM element through `textContent` and passed as
     * that element, which SweetAlert2 appends verbatim: the same text on
     * screen, inert when the message is not plain text. The same rule holds
     * for every SweetAlert2 option that renders a string (`html`, `title`,
     * `footer`, ...): never pass a server-originated string as HTML.
     *
     * @param {Error} err the rejection raised by the failed request.
     * @returns {Promise} the SweetAlert2 result promise for the dialog.
     */
    showResetFailure(err) {
      const detail = this.readableFailureDetail(err);
      const options = {
        icon: "error",
        title: "We could not reset your password",
        text: "The 6-digit code may have expired or already been used. Request a new code and try again.",
        showCloseButton: true,
      };

      if (detail !== "") {
        // WHY a DOM element and not the string itself: see the untrusted
        // text rule in this method's JSDoc. `textContent` renders the
        // detail as text, so a message that happens to contain markup is
        // shown literally instead of being parsed into the live document.
        const footer = document.createElement("span");
        footer.textContent = detail;
        options.footer = footer;
      }
      return Swal.fire(options);
    },

    /**
     * Clears the form after a successful reset.
     */
    resetForm() {
      this.formDate.newPassword = "";
      this.confirmPassword = "";
      this.formDate.token = "";
    },

    /**
     * Runs one password reset.
     *
     * FROZEN PAYLOAD CONTRACT (TicketApp #29691, task 002):
     *   * `formDate` is dispatched AS IT IS. Its wire shape is
     *     { token, newPassword } and nothing else -- `token` is the wire name
     *     of the 6-digit code, not a delivery channel.
     *   * The Vuex action `confirmReset` (src/store/modules/auth.js) MUTATES
     *     this very object in place -- it adds its own `localID` -- and then
     *     PUTs it to /auth/confirmReset with a FRESH localid for every call.
     *     It never routes through `sendRequest`, so the login-side identity
     *     policy does not apply to this flow and must not be "corrected" here.
     *   * Because of that in-place mutation, every piece of screen state that
     *     is not part of the wire payload (the confirmation value, the two
     *     show-password flags) lives OUTSIDE this object.
     *   * Do not replace this dispatch with `sendRequest`, do not rename the
     *     payload properties and do not trim them. The backend binds `token`
     *     and `newPassword` only.
     *
     * TERMINAL STATES: every exit from this method ends in something the
     * operator can see -- a success toast, a guidance modal for a form that is
     * not ready, or an error modal for a failed request. There is no silent
     * path.
     *
     * @returns {Promise} resolves when the reset has been handled; rejects
     *   with the original error after the failure modal has been shown.
     */
    async submitForm() {
      // WHY the guard sits here as well as on the button: the SAVE button is
      // disabled while a request is in flight, but a browser can still
      // submit the form from a text field, and the reset code is single-use.
      // Two dispatches would spend the same code twice and the second one
      // would fail for a reason the operator cannot act on.
      if (this.loading) {
        return;
      }

      const guidance = this.validateForm();
      if (guidance !== "") {
        this.trackReset("fail");
        return this.showGuidanceModal(guidance);
      }

      this.loading = true;
      this.trackReset("submit");

      try {
        await this.$store.dispatch("confirmReset", this.formDate);
        this.trackReset("success");
        responses.showSuccess("Password reset successfully");
        this.resetForm();
        this.$router.push("/");
      } catch (err) {
        this.trackReset("fail");
        // WHY the error modal is raised here, before the rejection is
        // re-thrown: the shared error handler opens NO dialog on this leg -
        // the store action calls it with the `isSweetalert` flag set (task
        // 003, S1 seam) - so this modal is the ONLY error dialog the
        // operator sees. The open-then-close race that used to swallow the
        // shared dialog is gone: task 005 (commit b0899f5) removed the
        // force-close, so the dialog the shared handler opens on the other
        // legs now stays visible. The re-throw is kept because callers rely
        // on the rejection propagating, and it now always happens AFTER the
        // visible feedback.
        this.showResetFailure(err);
        throw err;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.style-chooser .vs__search::placeholder,
.style-chooser .vs__dropdown-toggle,
.style-chooser .vs__dropdown-menu {
  background: #dfe5fb;
  border: none;
  color: #394066;
  text-transform: lowercase;
  font-variant: small-caps;
}

.page {
  height: 100%;
  width: 100%;
  background: transparent;
  overflow-x: hidden;
  position: relative;
}

.list-body {
  padding: 10px;
  width: 100%;
  max-width: 600px;
  margin: 20px auto;
}

.col-md-4 {
  margin: 5px 0 0;
}

/* Support text under a field: the delivery channel, the password guidance
   and the live match message all share this quiet, readable style. */
.form-hint {
  margin: 0;
  font-size: 0.85rem;
  line-height: 1.35;
  color: #5a6270;
}

.form-hint-match {
  color: #1a7f4b;
  font-weight: 600;
}

.form-hint-mismatch {
  color: #b02a37;
  font-weight: 600;
}

.show-password-checkbox {
  margin: 0;
  font-size: 0.9rem;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  cursor: pointer;
}
</style>
